/**
 * Patches Cache
 *
 * WHY THIS EXISTS:
 * The PatchInput dropdown uses on-demand loading - it only fetches patches from the API
 * when the user interacts with it. However, once a patch is selected, other parts of the
 * UI need access to the full patch object (not just the ID). This cache stores patches
 * as they're fetched, making them available for lookups.
 *
 * WITHOUT THIS CACHE:
 * - User selects "London Central" patch (ID: 123)
 * - Component only has the ID (123), not the patch object
 * - Cannot display the patch name in the bulk edit banner
 * - Cannot check if patch is compatible when switching departments
 *
 * WITH THIS CACHE:
 * - Dropdown fetches patches → cache stores them
 * - User selects patch → lookup by ID returns full object
 * - UI can display names, check compatibility, etc.
 *
 * USER SCENARIOS:
 * 1. Bulk Edit Banner: Shows "Editing 3 areas: London Central, Manchester, Birmingham"
 *    → Needs patch names from cache via selectedAreaNames computed
 *
 * 2. Department Switch: User switches from Sales to Lettings
 *    → Needs to check if selected patches are compatible (sale/let/both)
 *    → Uses findPatchById() + isPatchCompatible() with cached patch data
 *
 * 3. Auto-switch Department: User selects a "sale-only" patch while in Lettings
 *    → Component auto-switches to Sales department
 *    → Needs patch.type from cache to determine correct department
 */

// Types
import type { Ref } from 'vue';
import type { TPatch } from '~/misc/patch/types';
import type { ICompanyPatchesCache } from './types';

export const useCompanyPatchesCache = (): ICompanyPatchesCache => {
  const patches: Ref<TPatch[]> = ref<TPatch[]>([]);

  // Merges new patches into the cache, filtering out duplicates by ID
  const addPatches = (newPatches: TPatch[]): void => {
    const existingIds: Set<number> = new Set(patches.value.map((p: TPatch) => p.id));
    const uniqueNewPatches: TPatch[] = newPatches.filter((p: TPatch) => !existingIds.has(p.id));
    patches.value = [...patches.value, ...uniqueNewPatches];
  };

  return {
    patches,
    addPatches,
  };
};
